import type * as hast from 'hast';

import type * as api from '@gitbook/api-client';
import {
    DEFAULT_INLINE_BUTTON_SIZE,
    DOCUMENT_ACTION_PARAMETERS,
    FRAGMENT_ANNOTATION_BODY,
    INLINE_ANNOTATION,
    INLINE_BUTTON,
    INLINE_BUTTON_SIZES,
    INLINE_LINK,
    isDocumentButtonAction,
    isDocumentSearchScope,
    mention,
} from '@gitbook/doc-core';
import { Fragment, Inline, type Text } from '@gitbook/slate';

import { toDataAttribute } from '../../utils';
import type { ParsingContext } from '../context';
import type { HastToGastTransformMap } from './types';
import { getBooleanAttribute, getClassNamesAttribute, getStringAttribute } from './utils';

export const transformInlineLinks: HastToGastTransformMap = {
    a: async (node: hast.Element, eat, context, parsingContext) => {
        const { href } = node.properties || {};

        if (!node.children.length) {
            return [];
        }

        const children = await eat('inlines', node.children, context);

        const classNames = getClassNamesAttribute(node.properties);

        if (classNames.includes('button')) {
            return transformButton(
                node,
                children,
                classNames,
                typeof href === 'string' ? href : undefined,
                parsingContext
            );
        }

        if (typeof href !== 'string') {
            return children;
        }

        if (getBooleanAttribute(node.properties, 'dataFootnoteRef')) {
            return transformFootnoteBackRef(node, children, parsingContext);
        }

        const ref = await parsingContext.resolveLink(href);
        if (!ref) {
            return children;
        }

        if (
            // An HTML mention will have a "mention" property
            typeof node.properties?.dataMention === 'string' ||
            // A mention from Markdown will have a title of "mention".
            node.properties?.title === 'mention'
        ) {
            return [mention.createMention(ref)];
        }

        // When creating inline links:
        // - we set the page a link title if it exists
        if (parsingContext.onCreateInlineLink) {
            parsingContext.onCreateInlineLink(ref, node.properties);
        }

        return [
            Inline.create({
                type: INLINE_LINK,
                data: { ref },
                nodes: children,
            }),
        ];
    },
    button: async (node: hast.Element, eat, context, parsingContext) => {
        if (!node.children.length) {
            return [];
        }

        const children = await eat('inlines', node.children, context);
        const classNames = getClassNamesAttribute(node.properties);

        if (!classNames.includes('button')) {
            return children;
        }

        return transformButton(node, children, classNames, undefined, parsingContext);
    },
};

/**
 * Transform a footnote back reference link (<a class="footnote-backref">) into an annotation.
 */
async function transformFootnoteBackRef(
    node: hast.Element,
    children: (Inline | Text)[],
    parsingContext: ParsingContext
) {
    const { href } = node.properties || {};

    if (!href || typeof href !== 'string' || !href.startsWith('#')) {
        return children;
    }

    const id = href.slice(1);
    const fragmentNodes = await parsingContext.getDefinitionForFootnoteId(id);

    if (fragmentNodes.length === 0) {
        return children;
    }

    return [
        Inline.create({
            type: INLINE_ANNOTATION,
            nodes: children,
            fragments: [
                Fragment.create({
                    fragment: FRAGMENT_ANNOTATION_BODY,
                    type: FRAGMENT_ANNOTATION_BODY,
                    nodes: fragmentNodes,
                }),
            ],
        }),
    ];
}

/**
 * Transform an anchor element that should be a button.
 */
async function transformButton(
    node: hast.Element,
    children: (Inline | Text)[],
    classNames: string[],
    href: string | undefined,
    parsingContext: ParsingContext
) {
    const label = children.reduce((str, child) => `${str}${child.text}`, '');
    const icon = getStringAttribute(node.properties, 'dataIcon');
    // Mirror of serialize: read the non-default size class; absence means the default (undefined).
    const size = INLINE_BUTTON_SIZES.find(
        (candidate) => candidate !== DEFAULT_INLINE_BUTTON_SIZE && classNames.includes(candidate)
    );

    const action = getStringAttribute(node.properties, 'dataAction');
    if (isDocumentButtonAction(action)) {
        const actionParameters = Object.fromEntries(
            DOCUMENT_ACTION_PARAMETERS[action].map((parameter) => [
                parameter,
                getStringAttribute(node.properties, toDataAttribute(parameter)) ?? '',
            ])
        );
        const scope = getStringAttribute(node.properties, 'dataScope');

        return [
            Inline.create({
                type: INLINE_BUTTON,
                isVoid: true,
                data: {
                    kind: classNames.includes('primary') ? 'primary' : 'secondary',
                    label,
                    action: {
                        action,
                        ...actionParameters,
                        ...(action === 'search' && isDocumentSearchScope(scope) ? { scope } : {}),
                    } as api.DocumentAction,
                    ...(size ? { size } : {}),
                    ...(icon ? { icon } : {}),
                },
            }),
        ];
    }

    if (typeof href !== 'string') {
        // For buttons without href, create a button without a ref property
        return [
            Inline.create({
                type: INLINE_BUTTON,
                isVoid: true,
                data: {
                    kind: classNames.includes('primary') ? 'primary' : 'secondary',
                    label,
                    ...(size ? { size } : {}),
                    ...(icon ? { icon } : {}),
                },
            }),
        ];
    }

    const ref = await parsingContext.resolveLink(href);
    if (!ref) {
        // If href couldn't be resolved, create a button without a ref property
        return [
            Inline.create({
                type: INLINE_BUTTON,
                isVoid: true,
                data: {
                    kind: classNames.includes('primary') ? 'primary' : 'secondary',
                    label,
                    ...(size ? { size } : {}),
                    ...(icon ? { icon } : {}),
                },
            }),
        ];
    }

    return [
        Inline.create({
            type: INLINE_BUTTON,
            isVoid: true,
            data: {
                kind: classNames.includes('primary') ? 'primary' : 'secondary',
                label,
                ref,
                ...(size ? { size } : {}),
                ...(icon ? { icon } : {}),
            },
        }),
    ];
}
